// Instruments de la Veillée, entièrement synthétisés (Web Audio) : rien à télécharger, tout se joue hors-ligne.
// Cordes pincées (harpe, luth, oud, pizzicati), souffles (flûte, cor, basson, accordéon), violon, cloches en
// synthèse FM (cloche, célesta, boîte à musique, marimba), nappes (cordes, chœur), percussions, et les bruits
// du monde (vent, pluie, vagues, feu, oiseaux, grillons, grenouilles, gouttes, mouettes, hibou, tonnerre).
// Chaque fonction programme une note à l'instant `t` (horloge du contexte) : elle marche en direct comme hors-ligne.

export interface Bus {
  ctx: BaseAudioContext;
  /** son direct (vers le mixage) */
  dry: AudioNode;
  /** envoi vers la réverbération */
  wet: AudioNode;
  /** bruit blanc partagé (2 s, en boucle) */
  noise: AudioBuffer;
}

export type MelodicId =
  | "harpe" | "luth" | "oud" | "pizz" | "guitare" | "flute" | "violon" | "cor" | "basson" | "accordeon"
  | "celesta" | "cloche" | "boite" | "marimba" | "glas";

export type PadId = "cordes" | "choeur" | "choeurOu" | "orgue" | "brume";

export type PercId = "tambour" | "taiko" | "shaker" | "tic" | "tac" | "pied" | "mains" | "coeur" | "bois" | "darbouka";

const MIN = 0.0001;
const waves = new WeakMap<BaseAudioContext, Map<string, PeriodicWave>>();

function wave(ctx: BaseAudioContext, key: string, harmonics: readonly number[]): PeriodicWave {
  let cache = waves.get(ctx);
  if (!cache) {
    cache = new Map();
    waves.set(ctx, cache);
  }
  let w = cache.get(key);
  if (!w) {
    const real = new Float32Array(harmonics.length + 1);
    const imag = new Float32Array(harmonics.length + 1);
    harmonics.forEach((h, i) => { imag[i + 1] = h; });
    w = ctx.createPeriodicWave(real, imag);
    cache.set(key, w);
  }
  return w;
}

/** Bruit blanc de 2 s, à partager entre toutes les voix d'un contexte. */
export function makeNoise(ctx: BaseAudioContext): AudioBuffer {
  const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** Réponse impulsionnelle d'une grande salle de pierre (bruit stéréo qui s'éteint en `seconds`). */
export function makeImpulse(ctx: BaseAudioContext, seconds = 4.2): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const x = i / length;
      // attaque douce (pas de « clac » au début) puis décroissance exponentielle
      const fadeIn = Math.min(1, i / (ctx.sampleRate * 0.012));
      data[i] = (Math.random() * 2 - 1) * fadeIn * Math.pow(1 - x, 2.6);
    }
  }
  return buf;
}

function route(bus: Bus, node: AudioNode, pan: number, wet: number): void {
  const p = bus.ctx.createStereoPanner();
  p.pan.value = Math.max(-1, Math.min(1, pan));
  node.connect(p);
  p.connect(bus.dry);
  if (wet > 0) {
    const s = bus.ctx.createGain();
    s.gain.value = wet;
    p.connect(s);
    s.connect(bus.wet);
  }
}

function noiseSource(bus: Bus, t: number, duration: number, offset = Math.random() * 1.8): AudioBufferSourceNode {
  const src = bus.ctx.createBufferSource();
  src.buffer = bus.noise;
  src.loop = true;
  src.start(t, offset);
  src.stop(t + duration);
  return src;
}

function filter(ctx: BaseAudioContext, type: BiquadFilterType, freq: number, q = 0.7): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  return f;
}

/** Enveloppe percussive : montée éclair puis extinction exponentielle. */
function strike(g: AudioParam, t: number, peak: number, decay: number, attack = 0.004): void {
  g.setValueAtTime(MIN, t);
  g.exponentialRampToValueAtTime(Math.max(MIN * 2, peak), t + attack);
  g.exponentialRampToValueAtTime(MIN, t + attack + decay);
}

/** Enveloppe tenue : attaque, maintien jusqu'à la fin de la note, relâche. */
function hold(g: AudioParam, t: number, peak: number, dur: number, attack: number, release: number): number {
  const end = t + Math.max(dur, attack + 0.02);
  g.setValueAtTime(MIN, t);
  g.exponentialRampToValueAtTime(Math.max(MIN * 2, peak), t + attack);
  g.setValueAtTime(Math.max(MIN * 2, peak), end);
  g.exponentialRampToValueAtTime(MIN, end + release);
  return end + release;
}

function vibrato(ctx: BaseAudioContext, target: AudioParam, t: number, end: number, rate: number, depthHz: number, delay: number): void {
  const lfo = ctx.createOscillator();
  lfo.frequency.value = rate;
  const depth = ctx.createGain();
  depth.gain.setValueAtTime(0, t);
  depth.gain.linearRampToValueAtTime(depthHz, t + delay + 0.25);
  lfo.connect(depth).connect(target);
  lfo.start(t);
  lfo.stop(end);
}

// ───────── Cordes pincées ─────────

interface PluckShape { harmonics: readonly number[]; decay: number; bright: number; gain: number; wet: number; bend?: number }

const PLUCKS: Record<"harpe" | "luth" | "oud" | "pizz" | "guitare", PluckShape> = {
  harpe: { harmonics: [1, 0.55, 0.32, 0.2, 0.12, 0.07, 0.04], decay: 2.6, bright: 9, gain: 0.3, wet: 0.45 },
  luth: { harmonics: [1, 0.8, 0.55, 0.4, 0.3, 0.2, 0.12, 0.08], decay: 1.5, bright: 7, gain: 0.39, wet: 0.3 },
  oud: { harmonics: [1, 0.9, 0.7, 0.45, 0.35, 0.22, 0.15, 0.1, 0.06], decay: 1.2, bright: 6, gain: 0.45, wet: 0.3, bend: 1.03 },
  pizz: { harmonics: [1, 0.6, 0.4, 0.25, 0.15, 0.08], decay: 0.45, bright: 6, gain: 0.52, wet: 0.25 },
  guitare: { harmonics: [1, 0.7, 0.5, 0.42, 0.3, 0.25, 0.18, 0.12, 0.09, 0.06], decay: 1.8, bright: 5, gain: 0.34, wet: 0.35 },
};

function pluck(bus: Bus, id: keyof typeof PLUCKS, t: number, f: number, vel: number, pan: number): void {
  const s = PLUCKS[id];
  const ctx = bus.ctx;
  const osc = ctx.createOscillator();
  osc.setPeriodicWave(wave(ctx, id, s.harmonics));
  if (s.bend) {
    osc.frequency.setValueAtTime(f * s.bend, t);
    osc.frequency.exponentialRampToValueAtTime(f, t + 0.07);
  } else {
    osc.frequency.setValueAtTime(f, t);
  }
  const lp = filter(ctx, "lowpass", Math.min(f * s.bright, 14000), 0.9);
  lp.frequency.setValueAtTime(Math.min(f * s.bright, 14000), t);
  lp.frequency.exponentialRampToValueAtTime(Math.max(f * 1.2, 250), t + s.decay * 0.7);
  const g = ctx.createGain();
  strike(g.gain, t, s.gain * vel, s.decay);
  osc.connect(lp).connect(g);
  route(bus, g, pan, s.wet);
  osc.start(t);
  osc.stop(t + s.decay + 0.05);
}

// ───────── Souffles et archets ─────────

interface BlowShape {
  type: OscillatorType; lp: number; q: number; attack: number; release: number; gain: number; wet: number;
  vib: [rate: number, depthCents: number, delay: number]; breath: number; scoop: number; detune?: number;
}

const BLOWS: Record<"flute" | "violon" | "cor" | "basson" | "accordeon", BlowShape> = {
  flute: { type: "sine", lp: 5000, q: 0.5, attack: 0.07, release: 0.18, gain: 0.12, wet: 0.4, vib: [5.2, 14, 0.18], breath: 0.05, scoop: 0 },
  violon: { type: "sawtooth", lp: 2600, q: 1.4, attack: 0.09, release: 0.22, gain: 0.15, wet: 0.35, vib: [5.8, 18, 0.12], breath: 0, scoop: -18 },
  cor: { type: "sawtooth", lp: 1300, q: 0.8, attack: 0.1, release: 0.3, gain: 0.165, wet: 0.45, vib: [4.6, 6, 0.3], breath: 0, scoop: -30 },
  basson: { type: "square", lp: 900, q: 1.2, attack: 0.05, release: 0.1, gain: 0.08, wet: 0.2, vib: [5, 10, 0.2], breath: 0.015, scoop: 0 },
  accordeon: { type: "square", lp: 2400, q: 0.7, attack: 0.04, release: 0.12, gain: 0.058, wet: 0.25, vib: [6.5, 0, 0], breath: 0, scoop: 0, detune: 9 },
};

function blow(bus: Bus, id: keyof typeof BLOWS, t: number, f: number, dur: number, vel: number, pan: number): void {
  const s = BLOWS[id];
  const ctx = bus.ctx;
  const g = ctx.createGain();
  const end = hold(g.gain, t, s.gain * vel, dur, s.attack, s.release);
  const lp = filter(ctx, "lowpass", s.lp, s.q);
  if (id === "cor") {
    // le cuivre s'ouvre en attaquant
    lp.frequency.setValueAtTime(s.lp * 0.35, t);
    lp.frequency.exponentialRampToValueAtTime(s.lp * 1.3, t + 0.12);
    lp.frequency.exponentialRampToValueAtTime(s.lp, t + 0.4);
  }
  const voices = s.detune ? [-s.detune, s.detune] : [0];
  for (const cents of voices) {
    const osc = ctx.createOscillator();
    osc.type = s.type;
    osc.frequency.setValueAtTime(s.scoop ? f * Math.pow(2, s.scoop / 1200) : f, t);
    if (s.scoop) osc.frequency.exponentialRampToValueAtTime(f, t + 0.09);
    osc.detune.value = cents;
    if (s.vib[1] > 0) vibrato(ctx, osc.frequency, t, end, s.vib[0], f * (Math.pow(2, s.vib[1] / 1200) - 1), s.vib[2]);
    osc.connect(lp);
    osc.start(t);
    osc.stop(end + 0.02);
  }
  if (id === "flute") {
    // un soupçon d'octave pour le timbre boisé
    const o2 = ctx.createOscillator();
    o2.frequency.value = f * 2;
    const g2 = ctx.createGain();
    g2.gain.value = 0.12;
    o2.connect(g2).connect(lp);
    o2.start(t);
    o2.stop(end + 0.02);
  }
  if (id === "accordeon") {
    // le soufflet qui respire
    const trem = ctx.createOscillator();
    trem.frequency.value = 5.5;
    const depth = ctx.createGain();
    depth.gain.value = 0.25 * s.gain * vel;
    trem.connect(depth).connect(g.gain);
    trem.start(t);
    trem.stop(end);
  }
  if (s.breath > 0) {
    const n = noiseSource(bus, t, Math.min(end - t, 0.5));
    const bp = filter(ctx, "bandpass", Math.min(f * 2.5, 9000), 2);
    const ng = ctx.createGain();
    strike(ng.gain, t, s.breath * vel, 0.3, 0.03);
    n.connect(bp).connect(ng);
    route(bus, ng, pan, s.wet);
  }
  lp.connect(g);
  route(bus, g, pan, s.wet);
}

// ───────── Cloches (synthèse FM) ─────────

interface BellShape { ratio: number; index: number; decay: number; gain: number; wet: number; octave: number }

const BELLS: Record<"celesta" | "cloche" | "boite" | "marimba" | "glas", BellShape> = {
  celesta: { ratio: 4, index: 1.6, decay: 1.4, gain: 0.25, wet: 0.5, octave: 1 },
  cloche: { ratio: 3.5, index: 3, decay: 3.6, gain: 0.155, wet: 0.6, octave: 0 },
  boite: { ratio: 5.01, index: 1.2, decay: 0.9, gain: 0.28, wet: 0.45, octave: 2 },
  marimba: { ratio: 4, index: 0.9, decay: 0.55, gain: 0.42, wet: 0.25, octave: 0 },
  glas: { ratio: 1.41, index: 5, decay: 6, gain: 0.16, wet: 0.7, octave: -1 },
};

function bell(bus: Bus, id: keyof typeof BELLS, t: number, f0: number, vel: number, pan: number): void {
  const s = BELLS[id];
  const ctx = bus.ctx;
  // une cloche trop aiguë redescend d'octave : le modulateur doit rester sous la limite de Nyquist
  let f = f0 * Math.pow(2, s.octave);
  while (f * s.ratio > 16000 && f > 200) f /= 2;
  const car = ctx.createOscillator();
  car.frequency.value = f;
  const mod = ctx.createOscillator();
  mod.frequency.value = f * s.ratio;
  const mg = ctx.createGain();
  mg.gain.setValueAtTime(f * s.index, t);
  mg.gain.exponentialRampToValueAtTime(Math.max(1, f * 0.02), t + s.decay * 0.5);
  mod.connect(mg).connect(car.frequency);
  const g = ctx.createGain();
  strike(g.gain, t, s.gain * vel, s.decay, 0.002);
  car.connect(g);
  route(bus, g, pan, s.wet);
  car.start(t);
  mod.start(t);
  car.stop(t + s.decay + 0.05);
  mod.stop(t + s.decay + 0.05);
}

/** Joue une note mélodique de n'importe quel instrument. */
export function playNote(bus: Bus, id: MelodicId, t: number, f: number, dur: number, vel: number, pan = 0): void {
  switch (id) {
    case "harpe": case "luth": case "oud": case "pizz": case "guitare":
      return pluck(bus, id, t, f, vel, pan);
    case "flute": case "violon": case "cor": case "basson": case "accordeon":
      return blow(bus, id, t, f, dur, vel, pan);
    case "celesta": case "cloche": case "boite": case "marimba": case "glas":
      return bell(bus, id, t, f, vel, pan);
  }
}

// ───────── Nappes et basse ─────────

const FORMANTS: Record<"choeur" | "choeurOu", [number, number, number]> = {
  choeur: [800, 1150, 2900], // « a »
  choeurOu: [350, 700, 2500], // « ou »
};

/** Accord tenu (cordes, chœur, orgue, brume) : `freqs` jouées ensemble pendant `dur`. */
export function playPad(bus: Bus, id: PadId, t: number, freqs: number[], dur: number, vel: number, brightness = 0.5): void {
  const ctx = bus.ctx;
  const choir = id === "choeur" || id === "choeurOu";
  const attack = id === "brume" ? 2.4 : id === "orgue" ? 0.25 : 1.1;
  const release = id === "brume" ? 2.5 : 1.4;
  const g = ctx.createGain();
  const level = (id === "choeur" ? 0.125 : id === "choeurOu" ? 0.08 : id === "orgue" ? 0.03 : id === "brume" ? 0.052 : 0.04) * vel / Math.sqrt(Math.max(1, freqs.length));
  const end = hold(g.gain, t, level, dur, attack, release);
  // entrée commune des voix : les trois formants d'une voyelle pour le chœur, un passe-bas sinon
  let input: AudioNode;
  if (choir) {
    const split = ctx.createGain();
    FORMANTS[id].forEach((fr, i) => {
      const bp = filter(ctx, "bandpass", fr, 6 + i * 2);
      const fg = ctx.createGain();
      fg.gain.value = [1.6, 1, 0.45][i];
      split.connect(bp);
      bp.connect(fg).connect(g);
    });
    input = split;
  } else {
    const cutoff = id === "orgue" ? 2200 : id === "brume" ? 700 + brightness * 600 : 900 + brightness * 1800;
    input = filter(ctx, "lowpass", cutoff, 0.6);
    input.connect(g);
  }
  for (const f of freqs) {
    for (const cents of id === "orgue" ? [0, 1200] : [-7, 7]) {
      const osc = ctx.createOscillator();
      osc.type = id === "orgue" ? "triangle" : "sawtooth";
      osc.frequency.value = f;
      osc.detune.value = cents;
      if (id !== "orgue") vibrato(ctx, osc.frequency, t, end, 4.4 + Math.random(), f * 0.004, 0.4);
      osc.connect(input);
      osc.start(t);
      osc.stop(end + 0.05);
    }
  }
  route(bus, g, 0, id === "brume" ? 0.8 : 0.55);
}

/** Basse ronde (triangle et sinus grave), ou bourdon tenu quand `drone`. */
export function playBass(bus: Bus, t: number, f: number, dur: number, vel: number, drone = false): void {
  const ctx = bus.ctx;
  const g = ctx.createGain();
  const end = drone
    ? hold(g.gain, t, 0.09 * vel, dur, 0.8, 1.2)
    : hold(g.gain, t, 0.11 * vel, Math.min(dur, 1.2), 0.012, 0.35);
  const lp = filter(ctx, "lowpass", drone ? 420 : 700, 0.8);
  const kinds: [OscillatorType, number][] = drone ? [["sawtooth", 1], ["sawtooth", 1.5]] : [["triangle", 1], ["sine", 0.5]];
  for (const [type, mult] of kinds) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = f * mult;
    if (drone) osc.detune.value = mult === 1 ? -4 : 4;
    osc.connect(lp);
    osc.start(t);
    osc.stop(end + 0.05);
  }
  lp.connect(g);
  route(bus, g, 0, drone ? 0.35 : 0.12);
}

// ───────── Percussions ─────────

function thump(bus: Bus, t: number, from: number, to: number, sweep: number, decay: number, peak: number, pan: number, wet: number): void {
  const ctx = bus.ctx;
  const osc = ctx.createOscillator();
  osc.frequency.setValueAtTime(from, t);
  osc.frequency.exponentialRampToValueAtTime(to, t + sweep);
  const g = ctx.createGain();
  strike(g.gain, t, peak, decay, 0.003);
  osc.connect(g);
  route(bus, g, pan, wet);
  osc.start(t);
  osc.stop(t + decay + 0.05);
}

function hiss(bus: Bus, t: number, type: BiquadFilterType, freq: number, q: number, decay: number, peak: number, pan: number, wet: number): void {
  const ctx = bus.ctx;
  const src = noiseSource(bus, t, decay + 0.05);
  const f = filter(ctx, type, freq, q);
  const g = ctx.createGain();
  strike(g.gain, t, peak, decay, 0.002);
  src.connect(f).connect(g);
  route(bus, g, pan, wet);
}

export function playPerc(bus: Bus, id: PercId, t: number, vel: number, pan = 0): void {
  switch (id) {
    case "tambour": // tambour sur cadre
      thump(bus, t, 190, 72, 0.09, 0.32, 0.5 * vel, pan, 0.25);
      hiss(bus, t, "bandpass", 900, 1, 0.07, 0.12 * vel, pan, 0.2);
      return;
    case "darbouka":
      thump(bus, t, 320, 150, 0.04, 0.16, 0.5 * vel, pan, 0.2);
      hiss(bus, t, "bandpass", 2400, 3, 0.05, 0.2 * vel, pan, 0.15);
      return;
    case "taiko":
      thump(bus, t, 95, 42, 0.28, 1.0, 0.47 * vel, pan, 0.4);
      hiss(bus, t, "lowpass", 380, 0.7, 0.22, 0.14 * vel, pan, 0.3);
      return;
    case "shaker":
      hiss(bus, t, "highpass", 5200, 0.7, 0.07, 0.28 * vel, pan, 0.1);
      return;
    case "tic":
      hiss(bus, t, "bandpass", 3600, 9, 0.03, 0.9 * vel, pan, 0.15);
      thump(bus, t, 2300, 2100, 0.01, 0.025, 0.15 * vel, pan, 0.1);
      return;
    case "tac":
      hiss(bus, t, "bandpass", 1500, 7, 0.04, 1.6 * vel, pan, 0.15);
      thump(bus, t, 900, 860, 0.01, 0.04, 0.12 * vel, pan, 0.1);
      return;
    case "pied": // le pont du navire sous les bottes
      thump(bus, t, 130, 48, 0.08, 0.28, 0.6 * vel, pan, 0.25);
      hiss(bus, t, "lowpass", 320, 0.7, 0.12, 0.2 * vel, pan, 0.2);
      return;
    case "mains":
      for (const d of [0, 0.011, 0.023]) hiss(bus, t + d, "bandpass", 1400, 1.2, d === 0.023 ? 0.14 : 0.012, 1.0 * vel, pan, 0.3);
      return;
    case "coeur":
      thump(bus, t, 62, 38, 0.1, 0.22, 0.75 * vel, 0, 0.05);
      thump(bus, t + 0.28, 58, 36, 0.1, 0.2, 0.5 * vel, 0, 0.05);
      return;
    case "bois":
      thump(bus, t, 1050, 1000, 0.01, 0.06, 0.44 * vel, pan, 0.2);
      thump(bus, t, 1580, 1500, 0.01, 0.04, 0.2 * vel, pan, 0.2);
      return;
  }
}

/** Glissando de harpe (le Conteur tisse, un trésor scintille). */
export function playGlissando(bus: Bus, id: MelodicId, t: number, freqs: number[], spacing: number, vel: number, pan = 0): void {
  freqs.forEach((f, i) => playNote(bus, id, t + i * spacing, f, spacing * 2, vel * (0.75 + 0.25 * Math.sin((i / freqs.length) * Math.PI)), pan + (i / freqs.length - 0.5) * 0.6));
}

/** Trombone qui se moque (échec critique dans un monde loufoque). */
export function playWahWah(bus: Bus, t: number, f: number, vel: number): void {
  const ctx = bus.ctx;
  for (let k = 0; k < 3; k++) {
    const start = t + k * 0.38;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    const from = f * Math.pow(2, -k / 12);
    osc.frequency.setValueAtTime(from, start);
    osc.frequency.exponentialRampToValueAtTime(from * (k === 2 ? 0.8 : 0.97), start + (k === 2 ? 0.9 : 0.32));
    const lp = filter(ctx, "lowpass", 600, 4);
    lp.frequency.setValueAtTime(400, start);
    lp.frequency.exponentialRampToValueAtTime(1600, start + 0.08);
    lp.frequency.exponentialRampToValueAtTime(500, start + 0.3);
    const g = ctx.createGain();
    hold(g.gain, start, 0.12 * vel, k === 2 ? 0.8 : 0.3, 0.03, 0.15);
    osc.connect(lp).connect(g);
    route(bus, g, 0, 0.3);
    osc.start(start);
    osc.stop(start + 1.2);
  }
}

// ───────── Bruits du monde ─────────

export type BedId = "vent" | "pluie" | "vagues" | "grondement" | "statique" | "braise";
export type TextureId = "feu" | "oiseaux" | "grillons" | "grenouilles" | "gouttes" | "mouettes" | "hibou" | "tonnerre" | "gouttesPluie";

/** Nappes continues (en boucle), chacune avec son volume réglable en douceur. */
export class Beds {
  private gains = new Map<BedId, GainNode>();

  constructor(private bus: Bus, private out: AudioNode) {}

  /** Volume cible de chaque nappe (absente = 0), atteint en `glide` secondes. */
  set(targets: Partial<Record<BedId, number>>, at: number, glide = 2.5): void {
    const ids: BedId[] = ["vent", "pluie", "vagues", "grondement", "statique", "braise"];
    for (const id of ids) {
      const target = targets[id] ?? 0;
      let g = this.gains.get(id);
      if (!g) {
        if (target <= 0) continue;
        g = this.create(id, at);
      }
      g.gain.cancelScheduledValues(at);
      g.gain.setTargetAtTime(target, at, glide / 3);
    }
  }

  private create(id: BedId, at: number): GainNode {
    const ctx = this.bus.ctx;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(this.out);
    const src = ctx.createBufferSource();
    src.buffer = this.bus.noise;
    src.loop = true;
    src.start(at, Math.random() * 1.9);
    const lfo = (rate: number, depth: number, param: AudioParam): void => {
      const o = ctx.createOscillator();
      o.frequency.value = rate;
      const d = ctx.createGain();
      d.gain.value = depth;
      o.connect(d).connect(param);
      o.start(at);
    };
    switch (id) {
      case "vent": {
        const bp = filter(ctx, "bandpass", 420, 0.9);
        lfo(0.07, 260, bp.frequency);
        const sway = ctx.createGain();
        sway.gain.value = 0.55;
        lfo(0.11, 0.35, sway.gain);
        src.connect(bp).connect(sway).connect(g);
        break;
      }
      case "pluie": {
        const hp = filter(ctx, "highpass", 1300, 0.5);
        const lp = filter(ctx, "lowpass", 7500, 0.5);
        const lvl = ctx.createGain();
        lvl.gain.value = 0.3;
        src.connect(hp).connect(lp).connect(lvl).connect(g);
        break;
      }
      case "vagues": {
        const lp = filter(ctx, "lowpass", 520, 0.6);
        lfo(0.083, 260, lp.frequency);
        const swell = ctx.createGain();
        swell.gain.value = 0.5;
        lfo(0.083, 0.45, swell.gain);
        src.connect(lp).connect(swell).connect(g);
        break;
      }
      case "grondement": {
        const lp = filter(ctx, "lowpass", 85, 1.2);
        const lvl = ctx.createGain();
        lvl.gain.value = 2.2;
        lfo(0.05, 0.8, lvl.gain);
        src.connect(lp).connect(lvl).connect(g);
        break;
      }
      case "statique": {
        const bp = filter(ctx, "bandpass", 2600, 1.6);
        const lvl = ctx.createGain();
        lvl.gain.value = 0.18;
        lfo(0.3, 0.1, lvl.gain);
        src.connect(bp).connect(lvl).connect(g);
        break;
      }
      case "braise": {
        const lp = filter(ctx, "lowpass", 260, 0.7);
        const lvl = ctx.createGain();
        lvl.gain.value = 0.7;
        src.connect(lp).connect(lvl).connect(g);
        break;
      }
    }
    this.gains.set(id, g);
    return g;
  }
}

/**
 * Textures faites d'évènements (crépitements, chants d'oiseaux…) : on les sème au hasard sur une mesure de
 * `span` secondes à partir de `t`, avec la densité `amount` (0 à 1).
 */
export function sowTexture(bus: Bus, id: TextureId, t: number, span: number, amount: number, r: () => number): void {
  if (amount <= 0) return;
  const ctx = bus.ctx;
  const at = (): number => t + r() * span;
  switch (id) {
    case "feu": {
      const count = Math.round(span * (3 + 9 * amount));
      for (let i = 0; i < count; i++) {
        const big = r() < 0.12;
        hiss(bus, at(), "bandpass", big ? 900 + r() * 700 : 1800 + r() * 4200, big ? 1.5 : 2.5, big ? 0.05 : 0.012 + r() * 0.02,
          (big ? 0.85 : 0.2 + r() * 0.55) * amount, r() * 0.8 - 0.4, 0.08);
      }
      return;
    }
    case "oiseaux": {
      const phrases = Math.round(span * 0.6 * amount + r() * 1.5);
      for (let p = 0; p < phrases; p++) {
        const start = at();
        const base = 2600 + r() * 2800;
        const notes = 2 + Math.floor(r() * 5);
        const pan = r() * 1.6 - 0.8;
        for (let k = 0; k < notes; k++) {
          const s = start + k * (0.07 + r() * 0.05);
          const osc = ctx.createOscillator();
          const f0 = base * (0.9 + r() * 0.3);
          osc.frequency.setValueAtTime(f0, s);
          osc.frequency.exponentialRampToValueAtTime(f0 * (r() < 0.5 ? 1.35 : 0.72), s + 0.05 + r() * 0.04);
          const g = ctx.createGain();
          strike(g.gain, s, 0.1 * amount, 0.07, 0.008);
          osc.connect(g);
          route(bus, g, pan, 0.35);
          osc.start(s);
          osc.stop(s + 0.12);
        }
      }
      return;
    }
    case "grillons": {
      const trains = Math.round(span * 1.6 * amount);
      for (let i = 0; i < trains; i++) {
        const s = at();
        const f = 4300 + r() * 600;
        const pan = r() * 1.4 - 0.7;
        for (let k = 0; k < 3; k++) {
          const st = s + k * 0.032;
          const osc = ctx.createOscillator();
          osc.frequency.value = f;
          const g = ctx.createGain();
          strike(g.gain, st, 0.045 * amount, 0.022, 0.004);
          osc.connect(g);
          route(bus, g, pan, 0.2);
          osc.start(st);
          osc.stop(st + 0.04);
        }
      }
      return;
    }
    case "grenouilles": {
      const croaks = Math.round(span * 0.5 * amount + r());
      for (let i = 0; i < croaks; i++) {
        const s = at();
        const f = 120 + r() * 90;
        const pan = r() * 1.4 - 0.7;
        for (let k = 0; k < 2 + Math.floor(r() * 2); k++) {
          const st = s + k * 0.17;
          const osc = ctx.createOscillator();
          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(f, st);
          osc.frequency.linearRampToValueAtTime(f * 0.8, st + 0.12);
          const lp = filter(ctx, "lowpass", 650, 5);
          const g = ctx.createGain();
          strike(g.gain, st, 0.12 * amount, 0.12, 0.02);
          osc.connect(lp).connect(g);
          route(bus, g, pan, 0.25);
          osc.start(st);
          osc.stop(st + 0.16);
        }
      }
      return;
    }
    case "gouttes":
    case "gouttesPluie": {
      const drops = Math.round(span * (id === "gouttes" ? 0.6 : 2.5) * amount + r());
      for (let i = 0; i < drops; i++) {
        const s = at();
        const f = (id === "gouttes" ? 900 : 2200) + r() * 1300;
        thump(bus, s, f, f * 0.62, 0.06, id === "gouttes" ? 0.25 : 0.05, (id === "gouttes" ? 0.14 : 0.04) * amount, r() * 1.6 - 0.8, id === "gouttes" ? 0.9 : 0.2);
      }
      return;
    }
    case "mouettes": {
      if (r() > 0.45 * amount + 0.1) return;
      const s = at();
      const pan = r() * 1.6 - 0.8;
      for (let k = 0; k < 2 + Math.floor(r() * 3); k++) {
        const st = s + k * 0.28;
        const osc = ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(1500 + r() * 300, st);
        osc.frequency.exponentialRampToValueAtTime(950, st + 0.22);
        const g = ctx.createGain();
        strike(g.gain, st, 0.05 * amount, 0.22, 0.03);
        osc.connect(g);
        route(bus, g, pan, 0.5);
        osc.start(st);
        osc.stop(st + 0.3);
      }
      return;
    }
    case "hibou": {
      if (r() > 0.35 * amount) return;
      const s = at();
      const pan = r() * 1.2 - 0.6;
      [[0, 0.22], [0.42, 0.5]].forEach(([d, len]) => {
        const osc = ctx.createOscillator();
        osc.frequency.setValueAtTime(390, s + d);
        osc.frequency.linearRampToValueAtTime(350, s + d + len);
        const g = ctx.createGain();
        hold(g.gain, s + d, 0.08 * amount, len, 0.05, 0.12);
        osc.connect(g);
        route(bus, g, pan, 0.7);
        osc.start(s + d);
        osc.stop(s + d + len + 0.2);
      });
      return;
    }
    case "tonnerre": {
      if (r() > 0.18 * amount) return;
      const s = at();
      hiss(bus, s, "highpass", 1800, 0.6, 0.08, 0.15 * amount, 0, 0.5);
      const src = noiseSource(bus, s, 5);
      const lp = filter(ctx, "lowpass", 160 + r() * 140, 0.9);
      const g = ctx.createGain();
      g.gain.setValueAtTime(MIN, s);
      g.gain.exponentialRampToValueAtTime(0.9 * amount, s + 0.15);
      g.gain.exponentialRampToValueAtTime(0.25 * amount, s + 1.2);
      g.gain.exponentialRampToValueAtTime(MIN, s + 4.6);
      src.connect(lp).connect(g);
      route(bus, g, r() * 0.8 - 0.4, 0.6);
      return;
    }
  }
}
